import React, { useMemo, useState } from "react";
import queries from "../helpers/queries";
import { withUser } from "../Contexts/user";
import Table from "../Components/darkBasicTable";
import { useQuery } from "@apollo/react-hooks";
import moment from "moment";
import excel from "../Components/excel";

const capitalizeWords = str => {
    return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
};

const DownlineReport = () => {

    const [filters, setFilters] = useState({
        month: (moment().month() + 1).toString(), // Current month (1-12)
        year: moment().year().toString(),
    });
    const [report, setReport] = useState("Personal Report");
    const [totalDownlines, setTotalDownlines] = useState(0);
    const [totalSalesUnit, setTotalSalesUnit] = useState(0);
    const [totalGroupSalesUnit, setTotalGroupSalesUnit] = useState(0);
    const [totalConvertUnit, setTotalConvertUnit] = useState(0);
    const [totalGroupConvertUnit, setTotalGroupConvertUnit] = useState(0);
    const [totalGroupNetPrice, setTotalGroupNetPrice] = useState(0);
    const [totalGroupSPAPrice, setTotalGroupSPAPrice] = useState(0);
    const [totalNetPrice, setTotalNetPrice] = useState(0);
    const [totalSPAPrice, setTotalSPAPrice] = useState(0);

    const [paginationState, setPaginationState] = useState({
        page: 1,
    });


    const columns = useMemo(
        () => {
            const NumberFormatter = new Intl.NumberFormat("en-GB", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
    
            return [
                {
                    Header: "Downline",
                    accessor: "user",
                    Cell: ({ value }) => (
                        <div>{capitalizeWords(value)}</div>
                    ),
                },
                {
                    Header: "Sales Unit",
                    accessor: "personalBookingSales",
                },
                {
                    Header: "Sales Net GDV",
                    accessor: "personalNetGdv",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Convert Units",
                    accessor: "convertUnit",
                },
                {
                    Header: "Convert Net GDV",
                    accessor: "convertNetGdv",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Convert Percentage",
                    accessor: "convertPercentage",
                    Cell: ({ value }) => (
                        <div>{NumberFormatter.format(value)} %</div>
                    ),
                },
                {
                    Header: "Net GDV Percentage",
                    accessor: "netGdvPercentage",
                    Cell: ({ value }) => (
                        <div>{NumberFormatter.format(value)} %</div>
                    ),
                },
                {
                    Header: "Total Net Price",
                    accessor: "totalNetPrice",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Total SPA Price",
                    accessor: "totalSPAPrice",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
            ];
        },
        []
    );

    const groupColumns = useMemo(
        () => {
            const NumberFormatter = new Intl.NumberFormat("en-GB", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            return [
                {
                    Header: "Downline",
                    accessor: "user",
                    Cell: ({ value }) => (
                        <div>{capitalizeWords(value)}</div>
                    ),
                },
                {
                    Header: "Group Sales Unit",
                    accessor: "groupBookingSales",
                },
                {
                    Header: "Group Sales Net GDV",
                    accessor: "groupPersonalNetGdv",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Group Convert Units",
                    accessor: "groupConvertUnit",
                },
                {
                    Header: "Group Convert Net GDV",
                    accessor: "groupConvertNetGdv",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Group Convert Percentage",
                    accessor: "groupConvertPercentage",
                    Cell: ({ value }) => (
                        <div>{NumberFormatter.format(value)} %</div>
                    ),
                },
                {
                    Header: "Group Net GDV Percentage",
                    accessor: "groupNetGdvPercentage",
                    Cell: ({ value }) => (
                        <div>{NumberFormatter.format(value)} %</div>
                    ),
                },
                {
                    Header: "Group Net Price",
                    accessor: "groupNetPrice",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
                {
                    Header: "Group SPA Price",
                    accessor: "groupSPAPrice",
                    Cell: ({ value }) => (
                        <div>RM {NumberFormatter.format(value)}</div>
                    ),
                },
            ]
        },
        []
    );

    // Calculate dateFrom and dateTo based on selected month and year
    const getDateRange = () => {
        const year = parseInt(filters.year);
        const month = parseInt(filters.month);
        
        // Start of selected month
        const dateFrom = moment({ year, month: month - 1, day: 1 }).format("YYYY-MM-DD");
        
        // Start of next month
        const dateTo = moment({ year, month: month - 1, day: 1 }).add(1, 'month').format("YYYY-MM-DD");
        
        return { dateFrom, dateTo };
    };

    const { dateFrom, dateTo } = getDateRange();

    const { loading: downlineSalesReportLoading, data: downlineSalesReportData } = useQuery(queries.FETCH_MANUAL_DOWNLINE_SALES_REPORT_AGENT, {
        fetchPolicy: "network-only",
        variables: {
            by: paginationState.by ? paginationState.by : undefined,
            dir: paginationState.dir ? paginationState.dir : undefined,
            dateFrom: dateFrom,
            dateTo: dateTo,
        },
        onCompleted: (data) => {
            
            if (data.fetchManualDownlineSalesReportAgent) {
                const downlineData = data.fetchManualDownlineSalesReportAgent.data;
                
                // Set total downlines
                setTotalDownlines(downlineData.length);
                
                // Calculate totals
                let totalSalesUnit = 0;
                let totalConvertUnit = 0;
                let totalGroupSalesUnit = 0;
                let totalGroupConvertUnit = 0;
                let totalGroupNetPrice = 0;
                let totalGroupSPAPrice = 0;
                let totalNetPrice = 0;
                let totalSPAPrice = 0;

                
                downlineData.forEach((item) => {
                    totalSalesUnit += item.personalBookingSales;
                    totalConvertUnit += item.convertUnit;
                    totalGroupSalesUnit += item.groupBookingSales;
                    totalGroupConvertUnit += item.groupConvertUnit;
                    totalGroupNetPrice += item.groupNetPrice;
                    totalGroupSPAPrice += item.groupSPAPrice;
                    totalNetPrice += item.totalNetPrice;
                    totalSPAPrice += item.totalSPAPrice;

                });
        
                // Update state with calculated totals
                setTotalSalesUnit(totalSalesUnit);
                setTotalConvertUnit(totalConvertUnit);
                setTotalGroupSalesUnit(totalGroupSalesUnit);
                setTotalGroupConvertUnit(totalGroupConvertUnit);
                setTotalGroupNetPrice(totalGroupNetPrice);
                setTotalGroupSPAPrice(totalGroupSPAPrice);
                setTotalNetPrice(totalNetPrice);
                setTotalSPAPrice(totalSPAPrice);
            }
        }
    });

    const NumberFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return (
        <div className="dashboard-container">
                  <h1 className="downline-title">MANUAL DOWNLINE REPORTS</h1>
            
            <div className="stats-card-container">
                <div className="stats-card">
                    <div className="stats-card-title">Total Recruits</div>
                    <div className="stats-card-value">{(totalDownlines)}</div>
                </div>
                
                <div className="stats-card">
                    <div className="stats-card-title">Total Sales Units</div>
                    <div className="stats-card-value">{(totalSalesUnit)}</div>
                </div>
                
                <div className="stats-card">
                    <div className="stats-card-title">Total Convert Units</div>
                    <div className="stats-card-value">{(totalConvertUnit)}</div>
                </div>
                
             
                
              

                <div className="stats-card">
                    <div className="stats-card-title">Total Group Sales Units</div>
                    <div className="stats-card-value">{(totalGroupSalesUnit)}</div>
                </div>
                
                <div className="stats-card">
                    <div className="stats-card-title">Total Group Convert Units</div>
                    <div className="stats-card-value">{(totalGroupConvertUnit)}</div>
                </div>

                <div className="stats-card">
                    <div className="stats-card-title">Total Net Price</div>
                    <div className="stats-card-value">{NumberFormatter.format(totalNetPrice)}</div>
                </div>

                <div className="stats-card">
                    <div className="stats-card-title">Total SPA Price</div>
                    <div className="stats-card-value">{NumberFormatter.format(totalSPAPrice)}</div>
                </div>

                <div className="stats-card">
                    <div className="stats-card-title">Total Group Net Price</div>
                    <div className="stats-card-value">{NumberFormatter.format(totalGroupNetPrice)}</div>
                </div>

                <div className="stats-card">
                    <div className="stats-card-title">Total Group SPA Price</div>
                    <div className="stats-card-value">{NumberFormatter.format(totalGroupSPAPrice)}</div>
                </div>
                
            </div>
            
            <div className="filter-row">
                <div className="filter-item">
                    <label>Month</label>
                    <select
                        className="date-input"
                        value={filters.month}
                        onChange={(e) => {
                            setFilters((prev) => ({
                                ...prev,
                                month: e.target.value,
                            }));
                        }}
                    >
                        <option value="1">January</option>
                        <option value="2">February</option>
                        <option value="3">March</option>
                        <option value="4">April</option>
                        <option value="5">May</option>
                        <option value="6">June</option>
                        <option value="7">July</option>
                        <option value="8">August</option>
                        <option value="9">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                    </select>
                </div>
                <div className="filter-item">
                    <label>Year</label>
                    <input
                        className="date-input"
                        type="number"
                        min="2020"
                        max="2030"
                        placeholder="YYYY"
                        onChange={(e) => {
                            e.persist();
                            setFilters((prev) => ({
                                ...prev,
                                year: e.target.value,
                            }));
                        }}
                        value={filters.year}
                    />
                </div>
                <button
                    className="reset-btn"
                    onClick={() => {
                        setFilters({
                            month: (moment().month() + 1).toString(),
                            year: moment().year().toString(),
                        });
                    }}
                >
                    Reset
                </button>
                <button 
                    className="search-btn"
                    onClick={() => {
                        let payload = [];
                        
                        if (downlineSalesReportData && downlineSalesReportData.fetchManualDownlineSalesReportAgent && downlineSalesReportData.fetchManualDownlineSalesReportAgent.data) {
                            downlineSalesReportData.fetchManualDownlineSalesReportAgent.data.forEach((item) => {
                                payload.push({
                                    Downline: item.user ? capitalizeWords(item.user) : "",
                                    // Personal Report Data
                                    PersonalUnitsSold: item.personalBookingSales || 0,
                                    PersonalSalesNetGDV: item.personalNetGdv || 0,
                                    PersonalConvertUnits: item.convertUnit || 0,
                                    PersonalConvertNetGDV: item.convertNetGdv || 0,
                                    PersonalConvertPercentage: item.convertPercentage || 0,
                                    PersonalNetGDVPercentage: item.netGdvPercentage || 0,
                                    PersonalTotalNetPrice: item.totalNetPrice || 0,
                                    PersonalTotalSPAPrice: item.totalSPAPrice || 0,
                                    // Group Report Data
                                    GroupUnitsSold: item.groupBookingSales || 0,
                                    GroupSalesNetGDV: item.groupPersonalNetGdv || 0,
                                    GroupConvertUnits: item.groupConvertUnit || 0,
                                    GroupConvertNetGDV: item.groupConvertNetGdv || 0,
                                    GroupConvertPercentage: item.groupConvertPercentage || 0,
                                    GroupNetGDVPercentage: item.groupNetGdvPercentage || 0,
                                    GroupNetPrice: item.groupNetPrice || 0,
                                    GroupSPAPrice: item.groupSPAPrice || 0,
                                });
                            });
                        }

                        excel({
                            excelData: payload,
                            fileName: `ManualDownlineReport-${dateFrom}-${dateTo}`,
                        });
                    }}
                >
                    Export
                </button>
            </div>
            
            <div className="report-tabs">
                <button 
                    className={`report-tab ${report === "Personal Report" ? "active" : ""}`}
                    onClick={() => setReport("Personal Report")}
                >
                    Personal Report
                </button>
                <button 
                    className={`report-tab ${report === "Group Report" ? "active" : ""}`}
                    onClick={() => setReport("Group Report")}
                >
                    Group Report
                </button>
            </div>
            
            <div className="report-table-wrapper">
                {report === "Personal Report" && (
                    <Table
                        columns={columns}
                        data={
                            downlineSalesReportData ? 
                            downlineSalesReportData.fetchManualDownlineSalesReportAgent 
                                ? downlineSalesReportData
                                    .fetchManualDownlineSalesReportAgent.data
                                : [] : []
                        }
                        updatePagination={setPaginationState}
                        paginationState={paginationState}
                        loading={downlineSalesReportLoading}
                    />
                )}
                {report === "Group Report" && (
                    <Table
                        columns={groupColumns}
                        data={
                            downlineSalesReportData
                                ? downlineSalesReportData
                                    .fetchManualDownlineSalesReportAgent.data
                                : []
                        }
                        loading={downlineSalesReportLoading}
                    />
                )}
            </div>
        </div>
    );
};

export default withUser(DownlineReport);